/**
 * TTJ PDP buy actions.
 *
 * One custom element, <ttj-pdp-buy-actions>, rendered by snippets/buy-buttons.liquid on the main product
 * page only. It does two things and nothing else:
 *
 * 1. "Buy it now" availability.
 *    The Shopify accelerated-checkout button can stay clickable-looking when the selected variant is sold
 *    out. The single source of truth for availability is the theme's own Add to Cart submit button:
 *    Liquid renders it `disabled` for a sold-out / unavailable variant and product-info.js toggles that
 *    attribute on every variant change (toggleSubmitButton). This file mirrors that state onto the payment
 *    button: when the submit button is disabled, Buy it now is marked unavailable (CSS greys it out, the
 *    wrapper shows a not-allowed overlay, the buttons are made inert/disabled). When the submit button
 *    becomes enabled again, everything this file changed is restored. Nothing is ever changed while the
 *    variant is available, so Shopify's accelerated checkout is untouched for available products.
 *
 * 2. Mobile sticky Add to Cart (max-width: 749px, the theme's mobile breakpoint).
 *    A compact bar (markup comes from the <template> inside this element) is appended to <body>. It is
 *    NOT inside the product column because that column runs a transform animation, which would turn
 *    position: fixed into position: absolute. The bar has no cart logic of its own: tapping it clicks the
 *    real Add to Cart submit button, so product-form.js handles the request, errors, loading state and the
 *    cart drawer / notification exactly like the normal button. Label, disabled state, loading state,
 *    price and variant name are mirrored from the main form (MutationObserver + the theme's variant-change
 *    event), so the two can never drift apart.
 *
 * No external libraries.
 */
(function () {
  'use strict';

  if (customElements.get('ttj-pdp-buy-actions')) return;

  var MOBILE_QUERY = '(max-width: 749px)';
  var BODY_CLASS = 'ttj-sticky-atc-on';
  var HEIGHT_VAR = '--ttj-sticky-atc-h';

  function text(node) {
    return node ? (node.textContent || '').replace(/\s+/g, ' ').trim() : '';
  }

  function formatMoney(value) {
    // Same rupee grouping the rest of the PDP uses (assets/ttj-indian-money.js), when it is loaded.
    if (value && window.TTJIndianMoney && typeof window.TTJIndianMoney.formatText === 'function') {
      return window.TTJIndianMoney.formatText(value);
    }
    return value;
  }

  class TtjPdpBuyActions extends HTMLElement {
    connectedCallback() {
      if (this._started) return;
      this._started = true;

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.setup.bind(this), { once: true });
      } else {
        this.setup();
      }
    }

    disconnectedCallback() {
      this._started = false;
      this.teardown();
    }

    setup() {
      if (!this.isConnected) return;

      this.productInfo = this.closest('product-info');
      // Main product page only: never inside the quick-add modal or a featured-product section.
      if (!this.productInfo || this.closest('quick-add-modal') || this.productInfo.dataset.originalSection) return;

      this.block = this.parentElement;
      this.form = this.block && this.block.querySelector('form[data-type="add-to-cart-form"]');
      this.submit = this.form && this.form.querySelector('.product-form__submit');
      if (!this.form || !this.submit) return;

      this.sectionId = this.dataset.sectionId || this.productInfo.dataset.section;
      this.rafId = 0;
      this.disposers = [];

      this.syncBuyNow = this.syncBuyNow.bind(this);
      this.scheduleRefresh = this.scheduleRefresh.bind(this);

      this.buildBar();

      // Add to Cart submit button: disabled / aria-disabled / loading class / label text.
      this.observe(this.submit, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
        childList: true,
        subtree: true,
        characterData: true,
      });

      // Shopify renders / re-renders the accelerated checkout buttons asynchronously.
      this.observe(this.form, { childList: true, subtree: true });

      // Price block (replaced with innerHTML on every variant change).
      var priceRoot = this.priceRoot;
      if (priceRoot) {
        this.observe(priceRoot, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class'] });
      }

      // Variant picker (the selected-variant JSON lives inside it).
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        var unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.scheduleRefresh);
        if (typeof unsubscribe === 'function') this.disposers.push(unsubscribe);
      }

      this.scheduleRefresh();
    }

    observe(target, options) {
      var observer = new MutationObserver(this.scheduleRefresh);
      observer.observe(target, options);
      this.disposers.push(function () {
        observer.disconnect();
      });
    }

    get priceRoot() {
      return this.productInfo.querySelector('#price-' + this.sectionId);
    }

    /* ---------- sticky bar ---------- */

    buildBar() {
      var template = this.querySelector('template');
      var source = template && template.content && template.content.firstElementChild;
      if (!source) return;

      // Only one bar per page (the old one can still be around for a moment after a product swap).
      document.querySelectorAll('[data-ttj-sticky-atc]').forEach(function (existing) {
        existing.remove();
      });

      this.bar = source.cloneNode(true);
      this.button = this.bar.querySelector('[data-ttj-sticky-button]');
      this.label = this.bar.querySelector('[data-ttj-sticky-label]');
      this.priceEl = this.bar.querySelector('[data-ttj-sticky-price]');
      this.variantEl = this.bar.querySelector('[data-ttj-sticky-variant]');
      this.spinner = this.bar.querySelector('.loading__spinner');
      document.body.appendChild(this.bar);

      this.button.addEventListener('click', this.onBarClick.bind(this));

      // Mobile only: body padding so the last content is reachable, and the floating buttons lift above the bar.
      this.mediaQuery = window.matchMedia(MOBILE_QUERY);
      this.onMediaChange = this.updateBodyState.bind(this);
      if (this.mediaQuery.addEventListener) {
        this.mediaQuery.addEventListener('change', this.onMediaChange);
      } else if (this.mediaQuery.addListener) {
        this.mediaQuery.addListener(this.onMediaChange);
      }

      if (typeof ResizeObserver === 'function') {
        var resizeObserver = new ResizeObserver(this.updateBodyState.bind(this));
        resizeObserver.observe(this.bar);
        this.disposers.push(function () {
          resizeObserver.disconnect();
        });
      }
      window.addEventListener('resize', this.onMediaChange, { passive: true });
      window.addEventListener('orientationchange', this.onMediaChange, { passive: true });
      this.updateBodyState();
    }

    updateBodyState() {
      if (!this.bar) return;
      var active = this.mediaQuery.matches;
      document.body.classList.toggle(BODY_CLASS, active);
      if (active) {
        document.documentElement.style.setProperty(HEIGHT_VAR, this.bar.offsetHeight + 'px');
      } else {
        document.documentElement.style.removeProperty(HEIGHT_VAR);
      }
    }

    onBarClick(event) {
      event.preventDefault();
      // The real button owns all cart behaviour. Do nothing if it cannot be used right now.
      if (this.submit.disabled || this.submit.getAttribute('aria-disabled') === 'true') return;
      this.submit.click();
    }

    selectedVariant() {
      var variantSelects = this.productInfo.querySelector('variant-selects');
      var json = variantSelects && variantSelects.querySelector('[data-selected-variant]');
      if (!json) return null;
      try {
        return JSON.parse(json.textContent);
      } catch (e) {
        return null;
      }
    }

    currentPrice() {
      var root = this.priceRoot;
      if (!root || root.classList.contains('hidden')) return '';
      var price = root.querySelector('.price');
      if (!price) return '';
      var node = price.classList.contains('price--on-sale')
        ? price.querySelector('.price-item--sale')
        : price.querySelector('.price__regular .price-item--regular');
      return formatMoney(text(node));
    }

    refreshBar() {
      if (!this.bar) return;

      var disabled = this.submit.hasAttribute('disabled');
      var busy = this.submit.classList.contains('loading') || this.submit.getAttribute('aria-disabled') === 'true';
      var mainLabel = text(this.submit.querySelector('span'));

      // Same label as the main button ("Add to cart" / "Sold out" / "Unavailable").
      if (mainLabel && this.label.textContent !== mainLabel) this.label.textContent = mainLabel;

      this.button.disabled = disabled;
      if (disabled || busy) {
        this.button.setAttribute('aria-disabled', 'true');
      } else {
        this.button.removeAttribute('aria-disabled');
      }
      this.button.classList.toggle('loading', this.submit.classList.contains('loading'));
      if (this.spinner) this.spinner.classList.toggle('hidden', !this.submit.classList.contains('loading'));
      this.bar.classList.toggle('is-unavailable', disabled);

      // Selected variant (hidden for single-variant products and for an unavailable combination).
      var variant = this.selectedVariant();
      var variantTitle = variant && variant.title && variant.title !== 'Default Title' ? variant.title : '';
      this.variantEl.textContent = variantTitle;
      this.variantEl.hidden = !variantTitle;

      var price = this.currentPrice();
      this.priceEl.textContent = price;
      this.priceEl.hidden = !price;
    }

    /* ---------- Buy it now ---------- */

    syncBuyNow() {
      var wrapper = this.form.querySelector('.shopify-payment-button');
      if (!wrapper) return;

      var unavailable = this.submit.hasAttribute('disabled');

      if (unavailable) {
        wrapper.setAttribute('data-ttj-unavailable', 'true');
        wrapper.setAttribute('aria-disabled', 'true');
      } else {
        wrapper.removeAttribute('data-ttj-unavailable');
        wrapper.removeAttribute('aria-disabled');
      }

      // Children (Shopify's button / accelerated-checkout host) become inert so nothing inside can be
      // focused or activated, including components that render in a shadow root.
      Array.prototype.forEach.call(wrapper.children, function (child) {
        if (unavailable) {
          if (!child.inert) {
            child.inert = true;
            child.setAttribute('data-ttj-inert', '');
          }
        } else if (child.hasAttribute('data-ttj-inert')) {
          child.inert = false;
          child.removeAttribute('data-ttj-inert');
        }
      });

      // Real <button> elements are also natively disabled (only the ones this file disabled are re-enabled).
      wrapper.querySelectorAll('button').forEach(function (button) {
        if (unavailable) {
          if (!button.disabled) {
            button.disabled = true;
            button.setAttribute('data-ttj-disabled', '');
          }
        } else if (button.hasAttribute('data-ttj-disabled')) {
          button.disabled = false;
          button.removeAttribute('data-ttj-disabled');
        }
      });
    }

    /* ---------- shared ---------- */

    scheduleRefresh() {
      if (this.rafId) return;
      var self = this;
      this.rafId = window.requestAnimationFrame(function () {
        self.rafId = 0;
        if (!self.isConnected) return;
        self.syncBuyNow();
        self.refreshBar();
      });
    }

    teardown() {
      if (this.rafId) {
        window.cancelAnimationFrame(this.rafId);
        this.rafId = 0;
      }
      (this.disposers || []).forEach(function (dispose) {
        dispose();
      });
      this.disposers = [];

      if (this.mediaQuery && this.onMediaChange) {
        if (this.mediaQuery.removeEventListener) {
          this.mediaQuery.removeEventListener('change', this.onMediaChange);
        } else if (this.mediaQuery.removeListener) {
          this.mediaQuery.removeListener(this.onMediaChange);
        }
        window.removeEventListener('resize', this.onMediaChange);
        window.removeEventListener('orientationchange', this.onMediaChange);
      }

      if (this.bar) {
        this.bar.remove();
        this.bar = null;
      }
      // A newer instance (product swap) may already own a bar; only clear the page state if none is left.
      if (!document.querySelector('[data-ttj-sticky-atc]')) {
        document.body.classList.remove(BODY_CLASS);
        document.documentElement.style.removeProperty(HEIGHT_VAR);
      }
    }
  }

  customElements.define('ttj-pdp-buy-actions', TtjPdpBuyActions);
})();
